import React, { useState, useEffect, useContext } from "react";
import { Card, Typography, Modal, Spin, Row, Col, Tag, Segmented, Space } from "antd";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  BarChartOutlined,
  PieChartOutlined,
  ApartmentOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

const GENDER_COLORS = {
  Male: "#0e497b",      // Deep Navy Blue
  Female: "#ec4899",    // Vibrant Pink
  NotUpdated: "#64748b",// Slate Grey
};

export default function TotalEmployees({ organisationId }) {
  const { authData, backUrl } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("department"); // 'department' | 'gender' | 'company'
  
  const [departmentData, setDepartmentData] = useState([]);
  const [organisationData, setOrganisationData] = useState([]);
  const [overallGender, setOverallGender] = useState({ total: 0, male: 0, female: 0, notUpdated: 0 });

  const [selectedOrg, setSelectedOrg] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [orgDataMap, setOrgDataMap] = useState({});

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const params = {};
        if (organisationId && organisationId !== "ALL") {
          params.organisation_id = organisationId;
        }
        const res = await axios.get(`${backUrl}/api/emp/employees/dashboard-summary/`, {
          params,
          headers: { Authorization: `Bearer ${authData?.access}` },
        });

        const data = res.data || {};
        
        // 1. Overall Stats (safely handle both top-level and nested gender_summary)
        const total = data.total_employees || 0;
        const male = data.male_employees ?? data.gender_summary?.male ?? 0;
        const female = data.female_employees ?? data.gender_summary?.female ?? 0;
        const notUpdated = data.not_updated_gender_count ?? data.gender_summary?.not_updated ?? Math.max(0, total - (male + female));

        setOverallGender({ total, male, female, notUpdated });

        // 2. Department Distribution
        setDepartmentData(data.department_distribution || []);

        // 3. Organisation Distribution
        const orgList = data.organisation_distribution || [];
        const mapObj = {};
        const formattedOrg = orgList.map((item) => {
          mapObj[item.organisation] = { 
            male: item.Male || 0, 
            female: item.Female || 0,
            notUpdated: item.NotUpdated || 0,
            total: item.Total || 0,
          };
          return {
            name: item.organisation,
            value: item.Total || 0,
            Male: item.Male || 0,
            Female: item.Female || 0,
            NotUpdated: item.NotUpdated || 0,
          };
        });

        setOrganisationData(formattedOrg);
        setOrgDataMap(mapObj);
      } catch (err) {
        console.error("Error fetching employee gender distribution:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [organisationId, backUrl, authData?.access]);

  // Gender Donut Data
  const genderPieData = [
    { name: "Male", value: overallGender.male },
    { name: "Female", value: overallGender.female },
    ...(overallGender.notUpdated > 0 ? [{ name: "Not Updated", value: overallGender.notUpdated }] : []),
  ].filter(i => i.value > 0);

  const getPieColor = (name) => {
    if (name === "Male") return GENDER_COLORS.Male;
    if (name === "Female") return GENDER_COLORS.Female;
    return GENDER_COLORS.NotUpdated;
  };

  const handleBarClick = (data) => {
    if (data && data.activeLabel) {
      setSelectedOrg(data.activeLabel);
      setIsModalOpen(true);
    }
  };

  const selectedOrgData =
    selectedOrg && orgDataMap[selectedOrg]
      ? [
          { name: "Male", value: orgDataMap[selectedOrg].male },
          { name: "Female", value: orgDataMap[selectedOrg].female },
          ...(orgDataMap[selectedOrg].notUpdated > 0 
            ? [{ name: "Not Updated", value: orgDataMap[selectedOrg].notUpdated }] 
            : [])
        ].filter(i => i.value > 0)
      : [];

  const formatDeptName = (name) => {
    if (!name) return "General";
    if (name.length > 13) {
      return name.replace(/^Facilities-/i, "").substring(0, 13) + "…";
    }
    return name;
  };

  return (
    <Card
      bordered={false}
      style={{
        borderRadius: 16,
        background: "#ffffff",
        boxShadow: "0 4px 16px rgba(15, 23, 42, 0.04)",
        border: "1px solid #e2e8f0",
        minHeight: 450,
      }}
      bodyStyle={{ padding: 20 }}
    >
      {/* Header Controls */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div>
          <Title level={4} style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 16, lineHeight: "1.2" }}>
            Workforce & Gender Analytics
          </Title>
          <Text style={{ color: "#64748b", fontSize: 12, display: "block", marginTop: 2 }}>
            Department distribution, gender ratio & company share
          </Text>
        </div>
        <div>
          <Segmented
            options={[
              { label: "Dept", value: "department", icon: <BarChartOutlined /> },
              { label: "Gender", value: "gender", icon: <PieChartOutlined /> },
              { label: "Company", value: "company", icon: <ApartmentOutlined /> },
            ]}
            value={viewMode}
            onChange={(v) => setViewMode(v)}
            size="small"
          />
        </div>
      </div>

      {/* Top Metric Summary Badges */}
      <Row gutter={[8, 8]} style={{ marginBottom: 14 }}>
        <Col span={6}>
          <div style={{ background: "#f8fafc", padding: "6px 4px", borderRadius: 8, textAlign: "center", border: "1px solid #e2e8f0" }}>
            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>Total</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>{overallGender.total}</div>
          </div>
        </Col>
        <Col span={6}>
          <div style={{ background: "#eff6ff", padding: "6px 4px", borderRadius: 8, textAlign: "center", border: "1px solid #bfdbfe" }}>
            <div style={{ fontSize: 11, color: "#1d4ed8", fontWeight: 600 }}>Male</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#1e40af" }}>{overallGender.male}</div>
          </div>
        </Col>
        <Col span={6}>
          <div style={{ background: "#fdf2f8", padding: "6px 4px", borderRadius: 8, textAlign: "center", border: "1px solid #fbcfe8" }}>
            <div style={{ fontSize: 11, color: "#be185d", fontWeight: 600 }}>Female</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#9d174d" }}>{overallGender.female}</div>
          </div>
        </Col>
        <Col span={6}>
          <div style={{ background: "#f1f5f9", padding: "6px 4px", borderRadius: 8, textAlign: "center", border: "1px solid #cbd5e1" }}>
            <div style={{ fontSize: 11, color: "#475569", fontWeight: 600 }}>Unspecified</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#334155" }}>{overallGender.notUpdated}</div>
          </div>
        </Col>
      </Row>

      {/* Main Content Area with Fixed Height Scroll Container */}
      <div style={{ height: 275, maxHeight: 275, overflowY: "auto", overflowX: "hidden", paddingRight: 2 }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <Spin size="large" />
            <div style={{ marginTop: 12, color: "#64748b", fontWeight: 600 }}>Loading Workforce Analytics...</div>
          </div>
        ) : viewMode === "department" ? (
          /* Department Distribution Bar Chart */
          <ResponsiveContainer width="100%" height={265}>
            <BarChart
              data={departmentData}
              margin={{ top: 10, right: 10, left: -15, bottom: 25 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis 
                dataKey="department" 
                tickFormatter={formatDeptName}
                tick={{ fill: "#64748b", fontSize: 10 }} 
                angle={-25} 
                textAnchor="end"
                interval={0}
                height={45}
              />
              <YAxis tick={{ fill: "#64748b", fontSize: 11 }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
              <Legend verticalAlign="top" wrapperStyle={{ paddingBottom: 6 }} />
              <Bar dataKey="Male" stackId="a" fill={GENDER_COLORS.Male} name="Male" radius={[0, 0, 3, 3]} />
              <Bar dataKey="Female" stackId="a" fill={GENDER_COLORS.Female} name="Female" />
              <Bar dataKey="NotUpdated" stackId="a" fill={GENDER_COLORS.NotUpdated} name="Unspecified" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : viewMode === "gender" ? (
          /* Gender Ratio Donut Chart */
          <ResponsiveContainer width="100%" height={265}>
            <PieChart>
              <Pie
                data={genderPieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={95}
                paddingAngle={4}
                label={(entry) => `${entry.name}: ${entry.value}`}
              >
                {genderPieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getPieColor(entry.name)} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          /* Company Share View - Executive Organisation Cards */
          <div style={{ paddingTop: 2 }}>
            <Row gutter={[10, 10]}>
            {organisationData.map((org, index) => {
              const totalStaff = overallGender.total || 1;
              const percent = Math.round((org.value / totalStaff) * 100);
              const malePct = Math.round((org.Male / (org.value || 1)) * 100);
              const femalePct = Math.round((org.Female / (org.value || 1)) * 100);
              const notUpdatedPct = Math.max(0, 100 - (malePct + femalePct));

              return (
                <Col span={24} key={org.name || index}>
                  <div
                    style={{
                      background: index === 0 ? "linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)" : "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                      border: "1px solid #cbd5e1",
                      borderRadius: 12,
                      padding: "14px 16px",
                      boxShadow: "0 2px 8px rgba(15, 23, 42, 0.03)",
                    }}
                  >
                    <Row justify="space-between" align="middle" style={{ marginBottom: 10 }}>
                      <Col>
                        <Space>
                          <ApartmentOutlined style={{ fontSize: 18, color: "#0e497b" }} />
                          <Text style={{ fontWeight: 800, color: "#0f172a", fontSize: 14 }}>
                            {org.name}
                          </Text>
                        </Space>
                      </Col>
                      <Col>
                        <Tag color="blue" style={{ borderRadius: 12, fontWeight: 700, padding: "2px 12px", fontSize: 12 }}>
                          {org.value} Staff ({percent}% Share)
                        </Tag>
                      </Col>
                    </Row>

                    {/* Multi-Color Progress Bar */}
                    <div style={{ height: 10, width: "100%", background: "#e2e8f0", borderRadius: 5, overflow: "hidden", display: "flex", marginBottom: 12 }}>
                      <div style={{ width: `${malePct}%`, background: GENDER_COLORS.Male, height: "100%" }} title={`Male: ${org.Male} (${malePct}%)`} />
                      <div style={{ width: `${femalePct}%`, background: GENDER_COLORS.Female, height: "100%" }} title={`Female: ${org.Female} (${femalePct}%)`} />
                      <div style={{ width: `${notUpdatedPct}%`, background: GENDER_COLORS.NotUpdated, height: "100%" }} title={`Unspecified: ${org.NotUpdated} (${notUpdatedPct}%)`} />
                    </div>

                    {/* Gender Breakdown Badges */}
                    <Row justify="space-between" align="middle">
                      <Col span={24}>
                        <Space size="large" wrap>
                          <span style={{ fontSize: 12, color: "#1e40af", fontWeight: 600 }}>
                            👔 Male: <strong>{org.Male}</strong> ({malePct}%)
                          </span>
                          <span style={{ fontSize: 12, color: "#be185d", fontWeight: 600 }}>
                            👗 Female: <strong>{org.Female}</strong> ({femalePct}%)
                          </span>
                          {org.NotUpdated > 0 && (
                            <span style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>
                              ❓ Unspecified: <strong>{org.NotUpdated}</strong> ({notUpdatedPct}%)
                            </span>
                          )}
                        </Space>
                      </Col>
                    </Row>
                  </div>
                </Col>
              );
            })}
          </Row>
        </div>
      )}
      </div>

      {/* Interactive Modal for Org Click */}
      <Modal
        title={`Gender Ratio Breakdown - ${selectedOrg}`}
        open={isModalOpen}
        footer={null}
        onCancel={() => setIsModalOpen(false)}
        centered
        width={400}
      >
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie
              data={selectedOrgData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={85}
              label={(entry) => `${entry.name}: ${entry.value}`}
            >
              {selectedOrgData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getPieColor(entry.name)} />
              ))}
            </Pie>
            <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </Modal>
    </Card>
  );
}
